<script setup lang="ts">
import Button from '@/components/ui/button/Button.vue'
import SidePanel from '@/components/ui/sidePanel/SidePanel.vue'
import { onBeforeMount, provide, watch } from 'vue'
import BaseTable from '../../../ui/components/baseTable/BaseTable.vue'
import Pagination from '@/components/ui/pagination/Pagination.vue'
import Input from '@/components/ui/input/Input.vue'
import EmptyTable from '@/components/ui/emty-table/EmptyTable.vue'
import ContextMenu from '@/components/ui/contextMenu/ContextMenu.vue'
import ConfirmationModal from '@/components/ui/confirmation-modal/ConfirmationModal.vue'
import SimpleTag from '@/@web/ui/components/SimpleTag.vue'
import { fireRiskApi } from '@/services/clients/AxiosClients'
import ExclusionZoneLayout from '../layouts/ExclusionZone.layout.vue'
import { ExclusionZoneRepositoryApiAdapter } from '@/@core/exclusion-zone/infra/ExclusionZoneRepositoryApi.adapter'
import { ExclusionZoneViewModel } from '../viewmodels/ExclusionZone.viewmodel'
import { MonitoredAreaRepositoryApiAdapter } from '@/@core/monitored-area/infra/monitored-area-repository-adapter/MonitoredAreaRepositoryApi.adapter'
import ExclusionZoneForm from '../components/ExclusionZoneForm.vue'

const repository = new ExclusionZoneRepositoryApiAdapter(fireRiskApi)
const monitoredAreaRepository = new MonitoredAreaRepositoryApiAdapter(fireRiskApi)

const vm = new ExclusionZoneViewModel(repository, monitoredAreaRepository)

provide('exclusion-zone-view-model', vm)

watch(vm.openForm, (value) => {
	if (!value) vm.resetState()
})

onBeforeMount(() => vm.getExclusionZones())
</script>

<template>
	<exclusion-zone-layout>
		<main>
			<div class="flex justify-between my-4">
				<div class="flex gap-4">
					<Button
						icon="fa6-solid:trash"
						text="excluir"
						class="!outline-none"
						:disabled="!vm.registry.entriesToDelete.value.length"
						:class="[!vm.exclusionZones.value?.length ? 'disabled:opacity-70' : '']"
						danger
						simple
						@click="vm.showDeletePopup.value = true"
					/>
					<Button
						text="cadastrar"
						@click="vm.openForm.value = true"
					/>
				</div>
				<div
					style="display: flex"
					class="gap-4"
				>
					<Input
						v-model="vm.registry.searchTerm.value"
						search
						placeholder="Pesquisar"
						@clear="vm.registry.searchTerm.value = ''"
					/>
					<Button
						:disabled="!vm.exclusionZones.value?.length"
						text="exportar"
						secondary
						simple
						@click="() => vm.exportDataTable()"
					/>
				</div>
			</div>

			<base-table
				:checked.sync="vm.registry.entriesToDelete.value"
				:colunas="vm.columns.value"
				:data="vm.registry.pageData.value"
				@sort="vm.registry.handleSort($event)"
			>
				<template #custom-edit="dado">
					<ContextMenu
						:item-id="dado.id"
						:options="vm.kebabOptions()"
						@editar="vm.loadExclusionZone(dado.id)"
					/>
				</template>

				<template #monitoredArea="dado">
					<div class="flex gap-1">
						<simple-tag :label="dado.monitoredAreaName" />
					</div>
				</template>
			</base-table>

			<pagination
				v-if="vm.registry.pageData.value?.length"
				:key="vm.registry.searchTerm.value"
				class="mt-4"
				:total="vm.exclusionZones.value?.length || 0"
				:page="vm.registry.page.value"
				:page-size="vm.registry.pageSize.value"
				@paginationSettings="vm.registry.onPageChange"
			/>

			<empty-table
				v-if="!vm.registry.pageData.value.length"
				:key="vm.registry.searchTerm.value"
				registry-name="Zona de exclusão"
				:search="vm.registry.searchTerm.value"
				@clear="vm.registry.searchTerm.value = ''"
			/>
			<side-panel
				:change-is-visible="() => (vm.openForm.value = false)"
				:is-visible="vm.openForm.value"
				backdrop
			>
				<exclusion-zone-form
					v-if="vm.exclusionZone.value"
					:loading="vm.fetching.value"
					@cancel="() => (vm.openForm.value = false)"
					@submit="() => vm.createExclusionZone()"
				/>
			</side-panel>
			<confirmation-modal
				v-if="vm.showDeletePopup.value"
				title="Tem certeza que deseja excluir essa(s) zona(s) de exclusão?"
				subtitle="A ação não poderá ser desfeita."
				:loading="vm.fetching.value"
				:items="vm.getAlertSettingsToDelete()"
				@delete="vm.deleteAlertSettings()"
				@cancel="vm.showDeletePopup.value = false"
			/>
		</main>
	</exclusion-zone-layout>
</template>

<style scoped>
:deep(.container__crudform__panel.container__crudform__panel__visible) {
	width: 528px;
}
</style>
